/**
 * Getting a run started and its result sent — without ever blocking Play on
 * the network (docs/GAME_DESIGN.md § 7). Pure orchestration over a
 * BackendClient; timers and clocks are injectable for tests.
 */
import { RULES_VERSION } from '@dropstack/core';
import type { BackendClient, LeaderboardScope, SessionTicket } from '../../contracts.ts';
import { localRatings, type Progress } from '../../state/progress.ts';
import { LEADERBOARD_SCOPES } from '../../services/backend/api-types.ts';

/**
 * The longest Play waits for a server ticket before starting an offline
 * (unranked) run. The backend probe itself takes up to 1.5 s at launch.
 */
export const START_TIMEOUT_MS = 2500;

/** Resolve with the promise's value, or null when it rejects or takes longer than `ms`. */
export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return new Promise<T | null>((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
    );
  });
}

/**
 * A ticket for an unranked run made entirely on this device — used only when
 * no backend answered in time. Warm-starts from the locally mirrored rating.
 */
export function localOfflineTicket(progress: Progress, seed: number, now: number, relaxed = false): SessionTicket {
  const { startRating, referenceRating } = localRatings(progress);
  return {
    sessionId: `local-${now.toString(36)}-${(seed >>> 0).toString(36)}`,
    token: '',
    seed: seed >>> 0,
    startRating,
    referenceRating,
    rulesVersion: RULES_VERSION,
    issuedAt: now,
    offline: true,
    relaxed,
  };
}

export interface StartTicketDeps {
  /** Resolves once the backend is chosen (the startup health probe). */
  backend: Promise<BackendClient>;
  progress: () => Progress;
  seed: () => number;
  now: () => number;
  timeoutMs?: number;
  /** Ask for a "More time" run (Settings → relaxedPace). Default false. */
  relaxed?: boolean;
  /**
   * Called when the server's ticket is for different game rules than this
   * bundle (the server was updated while this page was open). The run then
   * plays on `ticket`, a local offline ticket, instead of a server run that
   * would only be refused at the end; the app should offer a reload.
   */
  onRulesMismatch?(info: { serverRulesVersion: number; ticket: SessionTicket }): void;
}

/**
 * Whether a ticket's rules match this bundle's core RULES_VERSION. Local
 * offline tickets always do (they are stamped with our own version).
 */
export function rulesMatch(ticket: SessionTicket): boolean {
  return ticket.rulesVersion === RULES_VERSION;
}

/**
 * A session ticket within `timeoutMs`: the backend's (server-signed, or the
 * offline demo's) when it answers in time and plays by this bundle's rules,
 * otherwise a local offline ticket. Never rejects.
 */
export async function startTicket(deps: StartTicketDeps): Promise<SessionTicket> {
  const relaxed = deps.relaxed ?? false;
  const attempt = (async () => {
    const backend = await deps.backend;
    // Registers the anonymous player if needed; startSession works without it too.
    await backend.ensurePlayer().catch(() => null);
    return backend.startSession({ relaxed });
  })();
  const ticket = await withTimeout(attempt, deps.timeoutMs ?? START_TIMEOUT_MS);
  if (ticket && rulesMatch(ticket)) return ticket;
  const local = localOfflineTicket(deps.progress(), deps.seed(), deps.now(), relaxed);
  if (ticket) deps.onRulesMismatch?.({ serverRulesVersion: ticket.rulesVersion, ticket: local });
  return local;
}

/**
 * The player's current rank on every rated all-time board they appear on —
 * used after they join the leaderboard from the game-over screen (their run
 * was already stored, so it now counts). Boards that fail to load are skipped.
 */
export async function fetchRanks(backend: BackendClient): Promise<Partial<Record<LeaderboardScope, number>>> {
  const pages = await Promise.allSettled(
    LEADERBOARD_SCOPES.map((scope) => backend.getLeaderboard({ metric: 'rated', scope, period: 'all', limit: 1 })),
  );
  const ranks: Partial<Record<LeaderboardScope, number>> = {};
  pages.forEach((page, i) => {
    const scope = LEADERBOARD_SCOPES[i];
    if (scope && page.status === 'fulfilled' && page.value.me) ranks[scope] = page.value.me.rank;
  });
  return ranks;
}
